import LogoIcon from '@/app/components/common/LogoIcon';
import {
  EmailAndNameQuestion,
  EmailAndNameAnswer,
} from '@/components/decision-tree/components/SurveyItem/components/Question/questionTypes/Email/types';
import { AnswerModel } from '@/components/decision-tree/hooks';
import { HeroVideo } from '@/components/hero-video/hero-video';
import customTheme from '@/constants/theme.custom';
import { TOAST_DEFAULT_OPTIONS } from '@/constants/toast';
import { useEnterKeyGlobal } from '@/hooks/use-enter-key';
import useVisitorSession from '@/hooks/useVisitorSession';
import authService from '@/services/auth.service';
import { Box, Button, Flex, Text, useToast } from '@cmpsr/components';
import { useCallback, useEffect, useRef, useState } from 'react';
import MailIcon from './MailIcon';
import ProfileIcon from './ProfileIcon';
import useEmailAndNameController from './EmailAndName.controller';
import InputField from './InputField';
import AuthNavigationButton from './AuthNavigationButton';
import { getRoute } from '@/utils';
import { ROUTE_SIGN_IN } from '@/constants/routes';

export type EmailAndNameProps = {
  question: EmailAndNameQuestion;
  defaultValue?: EmailAndNameAnswer;
  saveAnswer: (payload: AnswerModel) => void;
  goToNextSurveyItem: () => void;
  isNextDisabled: boolean;
};

export const EmailAndName = ({ question, defaultValue, saveAnswer, goToNextSurveyItem }: EmailAndNameProps) => {
  const initialSaveDone = useRef(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const toast = useToast();
  const { captureLeadAndName } = useVisitorSession();
  const { onInputFn, form, validationOptions, onBlurFn } = useEmailAndNameController({
    validationValues: question?.validationValues,
    saveAnswer,
    defaultValue,
  });

  // If email is prefilled run the saveAnswer function to validate the form
  useEffect(() => {
    if (defaultValue?.value && !initialSaveDone.current && saveAnswer) {
      // Save the full emailAndName value if present
      saveAnswer({
        type: 'emailAndName',
        value: {
          type: 'emailAndName',
          email: defaultValue.value.email ?? '',
          firstName: defaultValue.value.firstName ?? '',
        },
      });
      initialSaveDone.current = true;
    }
  }, [defaultValue, saveAnswer]);

  // Create lead and identify amplitude user
  const handleEmailCapture = useCallback(
    async (answer: EmailAndNameAnswer) => {
      try {
        // Get invite code from localStorage if present
        const inviteCode = authService.getInviteCode();
        const emailValue = answer.value.email;

        // Capture Lead and Name
        await captureLeadAndName({
          email: emailValue as string,
          inviteCode: inviteCode || undefined,
          firstName: answer.value.firstName ?? undefined,
        });
      } catch (err) {
        console.error('Failed to capture lead:', err);
        // Continue the flow even if lead creation fails
      }
    },
    [captureLeadAndName]
  );

  // Handle next button click: validate form, create lead, and go to next survey item
  const handleNextClick = async () => {
    setIsLoading(true);
    try {
      // Trigger form validation for all fields
      const isValid = await form.trigger();

      if (!isValid) {
        return;
      }

      // Get the current email value from the form
      const values = form.getValues();
      await handleEmailCapture({
        type: 'emailAndName',
        value: {
          type: 'emailAndName',
          email: values.email,
          firstName: values.firstName,
        },
      });
      await goToNextSurveyItem();
    } catch (error) {
      toast({
        ...TOAST_DEFAULT_OPTIONS,
        title: 'Error',
        description: error,
        status: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEnterKeyGlobal(handleNextClick);

  return (
    <Box width="100%" height="100vh" background="white" display="flex" flexDirection="column">
      <Box display="flex" flexDirection="column" flex={1} overflow="hidden">
        <Box width="100%" height="100%" overflow="hidden" position="relative">
          <HeroVideo src="/videos/catch-hero.mp4">
            <Flex flexDirection="column" alignItems="center" justifyContent="center" gap="8px" maxWidth="25rem">
              <LogoIcon color="white" width="2.6875rem" height="2.68744rem" />
              <Text color="white" fontSize="1.5625rem" fontWeight="400" lineHeight="130%">
                Preventing Cancer Starts with Knowing Your Risk
              </Text>
            </Flex>
          </HeroVideo>
        </Box>
        {/* Modal */}
        <Box
          width="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
          pt="spacer-8"
          pb="spacer-16"
          px={{ base: 'spacer-8', lg: 'spacer-16' }}
          mt="-48px"
          borderRadius="2rem"
          bgGradient="radial(circle, white, #F5F5F7)"
          zIndex="1"
        >
          <Box display="flex" flexDirection="column" gap="spacer-8" maxWidth="45rem" width="480px">
            <Flex flexDirection="column" gap="1.5rem">
              <Flex
                flexDirection="row"
                width="100%"
                justifyContent="space-between"
                gap="spacer-8"
                p="4px"
                bg="#EAECF0"
                borderRadius="32px"
              >
                <AuthNavigationButton to="/signup" active disabled>
                  Sign Up
                </AuthNavigationButton>
                <AuthNavigationButton to={getRoute(ROUTE_SIGN_IN)}>Login</AuthNavigationButton>
              </Flex>
              <InputField
                name="firstName"
                placeholder="First Name"
                type="text"
                autoComplete="given-name"
                icon={ProfileIcon}
                registerProps={form.register('firstName', validationOptions.firstName)}
                onBlur={onBlurFn}
                onInput={onInputFn}
                errorMessage={form.formState.errors.firstName?.message}
                defaultValue={defaultValue?.value?.firstName ?? undefined}
                autoFocus
              />
              <InputField
                name="email"
                placeholder="Email Address"
                type="email"
                autoComplete="email"
                icon={MailIcon}
                registerProps={form.register('email', validationOptions.email)}
                onBlur={onBlurFn}
                onInput={onInputFn}
                errorMessage={form.formState.errors.email?.message}
                defaultValue={defaultValue?.value?.email ?? undefined}
              />
              <Button
                variant="primary"
                backgroundColor="salmon-light"
                color="darkBlue"
                fontFamily={customTheme.typography.fonts.inter}
                type="submit"
                isLoading={isLoading}
                onClick={handleNextClick}
                width="100%"
                px={{ base: 'spacer-5', lg: 'spacer-8' }}
                py={{ base: 'spacer-5', lg: 'spacer-5' }}
                _hover={{
                  backgroundColor: 'white-100',
                }}
                _selected={{
                  backgroundColor: 'white-100',
                }}
                _focus={{
                  backgroundColor: 'white-100',
                }}
              >
                Get Started
              </Button>
            </Flex>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
